import { IPromise, IQService, IRootScopeService } from "angular";
import { IAssignee, IAssigneesStoreState, searchAssignees } from "@gtmhub/assignees";
import dayjs from "@gtmhub/dayjs";
import { EntityService } from "@gtmhub/entities/services/EntityService";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { RoleService } from "@gtmhub/roles";
import { IPlanningSession, ISessionsStoreState } from "@gtmhub/sessions";
import { SearchService } from "@gtmhub/shared/services/search.service";
import { IEntityData } from "@gtmhub/sockets";
import { INgRedux } from "@gtmhub/state-management";
import { TagService } from "@gtmhub/tags/tagService";
import { unique } from "@gtmhub/util";
import { byTextFieldAscAlphanumericCaseInsensitive } from "@gtmhub/util/sorting-utils";
import { Search } from "@webapp/search/models/search.models";
import { GridFiltersTagFactory } from "./grid-filters-tag-factory";
import { DynamicValueType, IArrayGridFilterDefinition, IGridFilterTag, entryDisplayName, entryValue } from "./models";

const byEndDescAndStartAsc = (a: IPlanningSession, b: IPlanningSession): -1 | 0 | 1 => {
  // "-end,start"
  const startA = dayjs(a.start);
  const startB = dayjs(b.start);
  const endA = dayjs(a.end);
  const endB = dayjs(b.end);

  if (endA.isSame(endB)) {
    return startA.isBefore(startB) ? -1 : startA.isAfter(startB) ? 1 : 0;
  } else {
    return endB.isBefore(endA) ? -1 : 1;
  }
};

const sessionsFilterByIdAndSortByEndAndStart = (sessions: IPlanningSession[], options: { excludedIds: string[] }) =>
  filterSessionsById(sessions, options.excludedIds).sort(byEndDescAndStartAsc);

const filterSessionsById = (sessions: IPlanningSession[], excludedIds: string[]) => sessions.filter((session) => excludedIds.indexOf(session.id) === -1);

/**
 * Returns top (tags with name starting with search condition) and other (tags with name only contained in search condition) matches from input based on provided tags
 * @param input search entered in input field
 * @param allTags full collection of tags to be filtered and sorted
 */
const getMatchesFromInput = (input: string, allTags: IGridFilterTag[]): { top: IGridFilterTag[]; other: IGridFilterTag[] } => {
  const topMatches: IGridFilterTag[] = [];
  const otherMatches: IGridFilterTag[] = [];
  allTags.forEach((tag) => {
    const tagNameStartsWithInput = tag.name.toLowerCase().startsWith(input.toLowerCase());
    const tagNameContainsInput = tag.name.toLowerCase().includes(input.toLowerCase());

    if (tagNameStartsWithInput) {
      topMatches.push(tag);
    } else if (tagNameContainsInput) {
      otherMatches.push(tag);
    }
  });
  return {
    top: topMatches,
    other: otherMatches,
  };
};

/**
 * Returns matching results in the following order: [tags with name starting with search condition sorted by name, tags with name only contained in search condition sorted by name]
 * @param input search entered in input field
 * @param allTags full collection of tags to be filtered and sorted
 */
const composeMatchingTags = (input: string, allTags: IGridFilterTag[]): IGridFilterTag[] => {
  const { top: topMatches, other: otherMatches } = getMatchesFromInput(input, allTags);

  topMatches.sort(byTextFieldAscAlphanumericCaseInsensitive("name"));
  otherMatches.sort(byTextFieldAscAlphanumericCaseInsensitive("name"));

  return topMatches.concat(otherMatches);
};

/**
 * Returns matching results in the following order: [tags with name starting with search condition in the provided order, tags with name only contained in search condition in the provided order]
 * @param input search entered in input field
 * @param allTags full collection of tags to be filtered and sorted
 */
const composeMatchingTagsInProvidedOrder = (input: string, allTags: IGridFilterTag[]): IGridFilterTag[] => {
  const { top: topMatches, other: otherMatches } = getMatchesFromInput(input, allTags);

  return topMatches.concat(otherMatches);
};

export const MAX_AUTOCOMPLETE_RESULTS = 15;

export class GridFiltersArrayQueryService {
  private roleTagValuesPromise: IPromise<IGridFilterTag[]>;

  static $inject = ["$q", "$rootScope", "$ngRedux", "RoleService", "SearchService", "TagService", "EntityService", "GridFiltersTagFactory", "UIErrorHandlingService"];

  constructor(
    private $q: IQService,
    private $rootScope: IRootScopeService,
    private $ngRedux: INgRedux,
    private roleService: RoleService,
    private searchService: SearchService,
    private tagService: TagService,
    private entityService: EntityService,
    private gridFiltersTagFactory: GridFiltersTagFactory,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {}

  private get assignees() {
    return this.$ngRedux.getState<IAssigneesStoreState>().assignees.items;
  }

  private get sessions() {
    return this.$ngRedux.getState<ISessionsStoreState>().sessions.items;
  }

  queryAssignees(input: string): IGridFilterTag[] {
    // 'Me' and 'My teams' will be added to autocomplete tags after checking limit
    const currentUserDynamicFilterTagsCount = 2;
    const limit = MAX_AUTOCOMPLETE_RESULTS - currentUserDynamicFilterTagsCount;
    return this.getFilteredAssigneesRedux({
      assigneeName: input,
      excludedIds: [],
      dynamicValueType: "me",
      limit,
    });
  }

  queryUsers(input: string): IGridFilterTag[] {
    // 'Me' will be added to autocomplete tags after checking limit
    const currentUserDynamicFilterTagsCount = 1;
    const limit = MAX_AUTOCOMPLETE_RESULTS - currentUserDynamicFilterTagsCount;
    return this.getFilteredAssigneesRedux({
      assigneeName: input,
      excludedIds: [],
      dynamicValueType: "me",
      limit,
      assigneeType: "user",
    });
  }

  queryTeams(input: string): IGridFilterTag[] {
    // 'My teams' will be added to autocomplete tags after checking limit
    const currentUserDynamicFilterTagsCount = 1;
    const limit = MAX_AUTOCOMPLETE_RESULTS - currentUserDynamicFilterTagsCount;
    return this.getFilteredAssigneesRedux({
      assigneeName: input,
      excludedIds: [],
      dynamicValueType: "myTeams",
      assigneeType: "team",
      limit,
    });
  }

  querySessions(input: string): IGridFilterTag[] {
    const sessionsDynamicFilterTagsCount = 1;
    const limit = MAX_AUTOCOMPLETE_RESULTS - sessionsDynamicFilterTagsCount;
    return this.getFilteredSessionsRedux({
      sessionTitle: input,
      limit,
    });
  }

  queryTags(input: string): IPromise<IGridFilterTag[]> {
    return this.tagService.getTags({ title: input }).then((tagsCollection) => {
      const tagTags = tagsCollection.tags.map<IGridFilterTag>((tag) => this.gridFiltersTagFactory.createSimpleTag(tag.title));
      return composeMatchingTags(input, tagTags);
    });
  }

  queryRoles(input: string): IPromise<IGridFilterTag[]> {
    if (!this.roleTagValuesPromise) {
      this.roleTagValuesPromise = this.roleService.getRoles().then((col) => col.items.map<IGridFilterTag>((role) => this.gridFiltersTagFactory.createRoleTag(role)));
    }

    return this.roleTagValuesPromise.then((roleTags) => composeMatchingTags(input, roleTags));
  }

  queryGoals(input: string): IPromise<IGridFilterTag[]> {
    return this.queryGoalsOrOkrs("goals", input);
  }

  queryOkrs(input: string): IPromise<IGridFilterTag[]> {
    return this.queryGoalsOrOkrs("okrs", input);
  }

  queryEntities(input: string, definition: IArrayGridFilterDefinition): IPromise<IGridFilterTag[]> {
    if (definition.arrayType !== "entity") {
      throw new Error("Specified filter definition is not of type 'entity'");
    }

    const { entity } = definition;
    const fields = unique([entity.displayNameField, entity.valueField]).join(",");

    const receiveDeferred = this.$q.defer<IGridFilterTag[]>();
    const unsubscribe = this.$rootScope.$on("entityDataResponse", (evt, entityData: IEntityData) => {
      if (entityData.entityName === entity.name) {
        const tags = entityData.entity.map<IGridFilterTag>((item) => ({ id: item[entity.valueField] as string, name: item[entity.displayNameField] as string }));
        receiveDeferred.resolve(tags);
      }
    });
    const receivePromise = receiveDeferred.promise;

    return this.entityService
      .requestEntityData(entity.name, { search: input, skip: 0, take: 15, fields, distinct: true })
      .then(() => receivePromise)
      .finally(unsubscribe);
  }

  queryStaticEntries(input: string, definition: IArrayGridFilterDefinition): IGridFilterTag[] {
    if (definition.arrayType !== "static") {
      throw new Error("Specified filter definition is not of type 'static'");
    }

    const entryTags = definition.entries.map<IGridFilterTag>((entry) => ({
      id: entryValue(entry).toString(),
      name: entryDisplayName(entry),
    }));

    return composeMatchingTagsInProvidedOrder(input, entryTags);
  }

  private queryGoalsOrOkrs(facetName: "goals" | "okrs", input: string): IPromise<IGridFilterTag[]> {
    const searchBody = this.searchService.getSearchBody(input, facetName);
    return this.searchService.getSomeSearchData<Search<"goals">>(searchBody, { skip: 0, take: 15 }).then(
      (searchResults) => searchResults.items.map<IGridFilterTag>((item) => this.gridFiltersTagFactory.createOkrTag(item)),
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
        return [];
      }
    );
  }

  private getFilteredAssigneesRedux(options: {
    assigneeName?: string;
    excludedIds: string[];
    dynamicValueType: DynamicValueType;
    limit: number;
    assigneeType?: "user" | "team";
  }): IGridFilterTag[] {
    const idsToExclude = new Set(options.excludedIds || []);

    const shouldSkip = (assignee: IAssignee): boolean => {
      return idsToExclude.has(assignee.id) || (options.assigneeType && assignee.type !== options.assigneeType);
    };

    const assigneeMatches = searchAssignees(options.assigneeName, this.assignees, { limit: options.limit, shouldSkip });

    return this.generateAutocompleteTags(assigneeMatches, {
      limit: options.limit,
      assigneeType: options.assigneeType,
      dynamicValueType: options.dynamicValueType,
    });
  }

  private generateAutocompleteTags(
    assignees: IAssignee[],
    options: { assigneeType: "user" | "team"; dynamicValueType: DynamicValueType; limit: number }
  ): IGridFilterTag[] {
    let tagsToDisplay: IGridFilterTag[] = [];

    for (const assignee of assignees) {
      tagsToDisplay.push(this.gridFiltersTagFactory.createAssigneeTag(assignee.id));

      if (!options.assigneeType && assignee.type === "team") {
        tagsToDisplay.push(this.gridFiltersTagFactory.createTeamPlusMembersTag(assignee.id));
        tagsToDisplay.push(this.gridFiltersTagFactory.createTeamOrgTag(assignee.id, { includeSubteamsAndMembers: true }));
      }

      if (options.assigneeType === "team") {
        tagsToDisplay.push(this.gridFiltersTagFactory.createTeamOrgTag(assignee.id));
      }

      if (tagsToDisplay.length >= options.limit) {
        tagsToDisplay = tagsToDisplay.slice(0, options.limit);
        break;
      }
    }

    if (!options.assigneeType && options.dynamicValueType === "me") {
      const myTeamsForAssigneeDynamicValueTag = this.gridFiltersTagFactory.createMyTeamsTag();
      tagsToDisplay.push(myTeamsForAssigneeDynamicValueTag);
    }

    const dynamicValueTag = options.dynamicValueType === "myTeams" ? this.gridFiltersTagFactory.createMyTeamsTag() : this.gridFiltersTagFactory.createMeTag();
    tagsToDisplay.push(dynamicValueTag);

    return tagsToDisplay;
  }

  private getFilteredSessionsRedux(options: { sessionTitle: string; limit: number }): IGridFilterTag[] {
    const sessionsCopy = sessionsFilterByIdAndSortByEndAndStart(this.sessions, { excludedIds: [] });
    const filteredSessions: IGridFilterTag[] = [];
    let matchesCount = 0;

    for (let i = 0; i < sessionsCopy.length; i++) {
      const session = sessionsCopy[i];
      const titleMatched = options.sessionTitle ? session.title.toLowerCase().includes(options.sessionTitle.toLowerCase()) : true;

      if (titleMatched) {
        filteredSessions.push(this.gridFiltersTagFactory.createSessionTag(session.id));
        matchesCount++;
      }

      if (options.limit <= matchesCount) {
        break;
      }
    }

    filteredSessions.push(this.gridFiltersTagFactory.createActiveSessionsTag());

    return filteredSessions;
  }
}
